import { getTextStylesFromBrandStyle } from 'modules/BrandDefinition';
import { CSSProperties } from 'react';
import { IMap } from 'typings/DeepIMap';
import * as Constants from 'const';
import { DefaultTextBrandStyle } from 'const/Styles';
import * as Models from 'models';
import { getDesktopBrandStyles } from 'utils/brandStyles';
import { brandBorderFromSource, brandBorderToSource, colorFromSource, colorToSource, colorToSourceTint } from 'utils/converters';
import { brandColorGradientFromSource, brandColorGradientToSource } from 'utils/converters/colorGradient';
import { getBorderCSSProperties, getPadding, getBorderRadius, getMinHeight } from 'utils/styles';
import { getCSSBackgroundBrand } from 'utils/styles/getBackground';

export const VERTICAL_ALIGNMENTS = [
  {
    type: Constants.AssetAlignmentType.TOP,
    style: { justifyContent: 'flex-start' },
  },
  {
    type: Constants.AssetAlignmentType.VERTICAL_CENTER,
    style: { justifyContent: 'center' },
  },
  {
    type: Constants.AssetAlignmentType.BOTTOM,
    style: { justifyContent: 'flex-end' },
  },
];

export type Styles = {
  backgroundColor?: Models.BrandColorMap;
  backgroundColorOpacity?: number;
  backgroundGradient?: Models.BrandColorGradientMap;
  backgroundImage?: Models.BackgroundImageMap;
  border?: Models.BorderMap;
  borderRadius?: Models.BorderRadiusMap;
  brandStyleVersion: number; // is needed to reset brandStyle
  brandStyle?: Models.BrandStyleMap;
  brandStyleChanged?: boolean;
  padding?: Models.PaddingMap;
  verticalAlignment?: Constants.AssetAlignmentType;
};

type SpecialSetters = {
  backgroundGradient: (gradient?: Models.BrandColorGradientMap, backupColor?: Models.BrandColorMap) => void;
  brandStyle: (brandStyle: Models.BrandStyleMap | undefined, version?: number) => void;
  brandStyleChanged: (brandStyleChanged?: boolean) => void;
  brandStyleVersion: never;
};

export type StylesSetters = {
  [K in keyof Omit<Styles, keyof SpecialSetters>]-?: (value: Styles[K]) => void;
} & {
  [K2 in keyof SpecialSetters as SpecialSetters[K2] extends never ? never : K2]: SpecialSetters[K2];
};

export function stylesFromSource(
  source: IMap<Models.TextRelationStyles>,
  brandStyles: Models.BrandStylesMap,
  colors: Models.BrandColorsList,
): Styles | null {

  const brandStyleId = source.get('brandStyleId');

  return {
    backgroundColor: colorFromSource(colors, source.get('backgroundColor'), source.get('backgroundColorTint')),
    backgroundColorOpacity: source.get('backgroundColorOpacity'),
    backgroundGradient: brandColorGradientFromSource(colors, source.get('backgroundGradient')),
    backgroundImage: source.get('backgroundImage'),
    border: brandBorderFromSource(colors, source.get('border')),
    borderRadius: source.get('borderRadius'),
    brandStyleVersion: 0,
    brandStyle: brandStyles.get(brandStyleId),
    brandStyleChanged: source.get('brandStyleChanged'),
    padding: source.get('padding'),
    verticalAlignment: source.getIn(['alignment', 'vertical']),
  };
}

export function stylesToSource(
  styles: Styles,
  source: IMap<Models.TextRelationStyles>,
): IMap<Models.TextRelationStyles> {
  return source.withMutations(values => values
    .set('backgroundColor', colorToSource(styles.backgroundColor))
    .set('backgroundColorTint', colorToSourceTint(styles.backgroundColor))
    .set('backgroundColorOpacity', styles.backgroundColorOpacity)
    .set('backgroundGradient', brandColorGradientToSource(styles.backgroundGradient))
    .set('backgroundImage', styles.backgroundImage)
    .set('border', brandBorderToSource(styles.border))
    .set('borderRadius', styles.borderRadius)
    .set('brandStyleChanged', styles.brandStyleChanged)
    .set('brandStyleId', styles.brandStyle?.get('id'))
    .set('padding', styles.padding)
    .setIn(['alignment', 'vertical'], styles.verticalAlignment),
  );
}

type CSSContext = {
  images: Models.CombinedDocumentsMap | Models.ImagesMap;
  placeholderMinHeight?: number;
};

export function stylesToCSS(styles: Styles, context: CSSContext): CSSProperties {
  const { images, placeholderMinHeight } = context;
  const aligment = VERTICAL_ALIGNMENTS.find(item => item.type === styles.verticalAlignment)?.style;

  const props: CSSProperties = {
    ...getBorderCSSProperties(styles.border),
    ...getPadding(styles.padding),
    ...getCSSBackgroundBrand(
      styles.backgroundColor,
      styles.backgroundColorOpacity,
      styles.backgroundGradient,
      styles.backgroundImage,
      images,
    ),
    ...getBorderRadius(styles.borderRadius),
    ...((aligment as CSSProperties) || {}),
    ...getMinHeight(placeholderMinHeight),
  };

  if (styles.brandStyle) {
    const breakpointStyles = getDesktopBrandStyles(styles.brandStyle);
    const textTransform = breakpointStyles?.get(Models.BrandStyleProp.TEXT_TRANSFORM);
    if (textTransform && textTransform !== 'none') {
      props.textTransform = textTransform;
    }
  }

  return props;
}

export function getInitialStyles(
  source: IMap<Models.TextRelationStyles>,
  colors: Models.BrandColorsList,
  fonts: Models.BrandFontsList,
  brandStyles: Models.BrandStylesMap,
): Styles {
  const styles = stylesFromSource(source, brandStyles, colors);
  if (!styles) {
    return {
      brandStyleVersion: 0,
    };
  }

  // revise brand style logic (the same in Artboard converter services/ArtboardConverter)
  if (!styles.brandStyleChanged) {
    if (!styles.brandStyle) {
      styles.brandStyle = brandStyles.get(DefaultTextBrandStyle);
    }
    const textStyles = getTextStylesFromBrandStyle(styles.brandStyle, colors, fonts);
    styles.backgroundColor = textStyles.backgroundColor;
    styles.backgroundGradient = undefined;
    styles.padding = textStyles.padding;
    styles.verticalAlignment = textStyles.verticalAlignment;
  }

  return styles;
}
